import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";

import Preloader from "./components/Preloader";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingCart from "./components/FloatingCart";
import ChatBot from "./components/ChatBot";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
import BookSession from "./pages/BookSession";
import EventPackages from "./pages/EventPackages";
import Shop from "./pages/Shop";
import ShopProductDetails from "./pages/ShopProductDetails";
import Contact from "./pages/Contact";
import Rentals from "./pages/StudioRentals";
import Checkout from "./pages/Checkout";
import StudioSessionBookingSuccess from "./pages/StudioSessionBookingSuccess";
import StudioRentalPaymentSuccess from "./pages/StudioRentalPaymentSuccess";
import EventPaymentSuccess from "./pages/EventPaymentSuccess";
import ShopOrderSuccess from "./pages/ShopOrderSuccess";
import Gallery from "./pages/Gallery";
import Cart from "./pages/Cart";
import Printing from "./pages/Printing";
import PrintOrderSuccess from "./pages/PrintOrderSuccess";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import {
  PortraitPhotography,
  FamilyPhotography,
  StudioPhotography,
} from "./pages/ServicePages";
import SEO from "./components/SEO";

import { CartProvider } from "./context/CartContext";

const PRIVATE_PATHS = [
  "/cart",
  "/checkout",
  "/studio-session-booking-success",
  "/studio-rental-payment-success",
  "/event-payment-success",
  "/shop-order-success",
  "/print-order-success",
];

const PRELOADER_SEEN_KEY = "kinews_preloader_seen";
const PRELOADER_DURATION_MS = 1200;

const hasSeenPreloader = () => {
  try {
    return sessionStorage.getItem(PRELOADER_SEEN_KEY) === "1";
  } catch {
    return false;
  }
};

const markPreloaderSeen = () => {
  try {
    sessionStorage.setItem(PRELOADER_SEEN_KEY, "1");
  } catch {
    // storage unavailable: preloader will simply show again next visit
  }
};

// ---- Inner app so we can read location (must be inside BrowserRouter) ----
function AppShell() {
  const location = useLocation();

  // Built pages carry their own <head> tags (scripts/seo-pages.js). Drop them
  // now that React renders its own, so nothing is duplicated.
  useLayoutEffect(() => {
    document
      .querySelectorAll("head [data-static-seo]")
      .forEach((el) => el.remove());
  }, []);

  const [loading, setLoading] = useState(
    location.pathname === "/" && !hasSeenPreloader(),
  );
  const hasShownHomePreloader = useRef(false);

  useEffect(() => {
    if (
      location.pathname === "/" &&
      !hasShownHomePreloader.current &&
      !hasSeenPreloader()
    ) {
      setLoading(true);
      const t = setTimeout(() => {
        setLoading(false);
        hasShownHomePreloader.current = true;
        markPreloaderSeen();
      }, PRELOADER_DURATION_MS);
      return () => clearTimeout(t);
    }

    setLoading(false);
  }, [location.pathname]);

  const isPrivatePage = PRIVATE_PATHS.includes(location.pathname);

  return (
    <div className="min-h-screen bg-[#1c1c1c] text-white">
      <ScrollToTop />
      {isPrivatePage && <SEO title="Kinews Studio Limited" noindex />}

      <AnimatePresence mode="wait">
        {loading ? (
          <Preloader key="preloader" />
        ) : (
          <div key="site">
            <Navbar />

            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/book" element={<BookSession />} />
                <Route path="/packages" element={<EventPackages />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:id" element={<ShopProductDetails />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/rentals" element={<Rentals />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route
                  path="/portrait-photographer-rochdale"
                  element={<PortraitPhotography />}
                />
                <Route
                  path="/family-photographer-rochdale"
                  element={<FamilyPhotography />}
                />
                <Route
                  path="/photography-studio-rochdale"
                  element={<StudioPhotography />}
                />
                <Route path="/printing" element={<Printing />} />
                <Route
                  path="/studio-session-booking-success"
                  element={<StudioSessionBookingSuccess />}
                />
                <Route
                  path="/studio-rental-payment-success"
                  element={<StudioRentalPaymentSuccess />}
                />
                <Route
                  path="/event-payment-success"
                  element={<EventPaymentSuccess />}
                />
                <Route
                  path="/shop-order-success"
                  element={<ShopOrderSuccess />}
                />
                <Route
                  path="/print-order-success"
                  element={<PrintOrderSuccess />}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>

            <Footer />
            <FloatingCart />
            <ChatBot />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </CartProvider>
    </HelmetProvider>
  );
}
